import {
  authedRequest,
  authedFormRequest,
  setFormData,
  objectToFormData,
} from './requests';
import { normalize, schema } from 'normalizr';
import dayjs from 'dayjs';
import { isString } from '../helpers';
import { utcOffsetMinutesFor } from '../utcOffsetMinutesFor';

const formatDateParam = (value) => {
  if (!value) return "";
  if (isString(value)) return value;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format() : "";
};
// Define schema
const channel = new schema.Entity('allChannels', {
  client: new schema.Entity('clients'),
  channel_theme_instance: new schema.Entity('channelThemeInstances'),
});

/**
 *
 * /channels resource requests
 *
 */
export const getChannels = (token, params) => authedRequest(`v2/channels${params}`, token).then(r => r);

/**
 * @typedef {Object} SlugAvailability
 * @property {string}  slug      The candidate, normalized lowercase by the server.
 * @property {boolean} available False only when some OTHER channel holds the
 *   candidate as its live slug or anywhere in its append-only `slug_history`.
 */

/**
 * Gallery-URL availability probe.
 *
 * GET v2/channel-slug-availability?slug=<candidate>[&channel_id=<id>]
 *   → 200 { slug, available } · 400 blank slug · 401 unauthenticated
 *   · 404 when `channel_id` names no channel the caller may update.
 *
 * Pass `channelId` when editing so that channel is excluded from the conflict
 * search and its owner can reclaim one of their own former URLs; omit it when
 * creating. This replaced a probe of `GET v2/channels/:slug` that only saw the
 * raw HTTP status: `Channel.lookup` resolves through `slug_history`, so a
 * channel's own former slug answered 200 and the dashboard reported
 * "already taken".
 *
 * The operation IS vendored: `src/api/openapi.snapshot.yaml` carries
 * `/api/v2/channel-slug-availability` (identified by path — it declares no
 * `operationId`), generated into `src/api/schema.d.ts`. The `SlugAvailability`
 * typedef above duplicates that shape for JSDoc's benefit; keep the two in step
 * by hand, because nothing checks them against each other.
 *
 * Note what that does and does not buy you: `yarn openapi:check` runs
 * `openapi:gen && git diff --exit-code`, regenerating the types FROM the
 * snapshot and diffing those. It proves the types match the snapshot — never
 * that the snapshot matches Rails, which CI has no checkout of. So drift
 * between this vendored copy and the real spec stays undetected: re-run
 * `yarn openapi:sync` against movebooth-rails to refresh it. Treat a green
 * `openapi:check` as evidence about the generated types alone.
 *
 * @param  {string}         token      Auth token.
 * @param  {string}         slug       Candidate gallery URL slug.
 * @param  {number|string=} channelId  Channel being edited; omit when creating.
 * @return {Promise<SlugAvailability>}
 */
export const checkSlugAvailability = (token, slug, channelId) => {
  const query = [`slug=${encodeURIComponent(slug)}`];
  // `!= null` deliberately covers both null and undefined — the create flow
  // passes neither, and a channel id is never legitimately 0.
  if (channelId != null) {
    query.push(`channel_id=${encodeURIComponent(channelId)}`);
  }
  return authedRequest(`v2/channel-slug-availability?${query.join('&')}`, token);
};



// Synthesize multi-mode / multi-style arrays from the legacy fields so the
// dashboard works against un-migrated Rails environments during the rollout
// window. Once Rails always emits these arrays the synthesis is a no-op.
const synthesizeEnabledModeIds = (channel) => {
  if (Array.isArray(channel.enabled_mode_ids) && channel.enabled_mode_ids.length) {
    return channel.enabled_mode_ids
      .map((v) => parseInt(v, 10))
      .filter((v) => !isNaN(v));
  }
  const legacyId = channel.mode ? channel.mode.id : null;
  return legacyId ? [parseInt(legacyId, 10)] : [];
};

const synthesizeEnabledImageStyles = (customization) => {
  if (!customization) return ["standard"];
  if (
    Array.isArray(customization.enabled_image_styles) &&
    customization.enabled_image_styles.length
  ) {
    return customization.enabled_image_styles;
  }
  if (customization.is_glitch_mode_enabled) return ["glitch"];
  if (customization.is_black_and_white_mode_enabled) return ["bw"];
  return ["standard"];
};

// Shared multi-mode / multi-style derivation used by both create (postChannel)
// and update (patchChannel). Produces the normalized arrays for the wire,
// plus — only when the array is single-valued — the legacy scalar fields
// so old iPads and un-migrated Rails environments still see a sensible
// default.
//
// Why "single-valued only": Rails' Channel#reconcile_mode_state and
// ChannelImageCustomization#reconcile_image_style_state callbacks treat a
// changed legacy scalar as the user's intent and NARROW the array to
// `[scalar]`. Sending mode_id=1 alongside enabled_mode_ids=[1, 2] on a
// new-record CREATE flips `mode_id_changed?` (nil → 1), which silently
// narrows the persisted array to [1] — the bug behind the
// multi-mode-new-channel.spec.js failure on staging. Same pattern for the
// boolean-vs-array pair on channel_image_customization. When the user has
// explicitly multi-selected, omit the scalar/booleans on the wire and let
// Rails derive them from `enabled_mode_ids.first` /
// `enabled_image_styles.first`.
const buildModeAndStyleParams = ({ enabled_mode_ids, mode_id, channel_image_customization }) => {
  const enabledModeIds = Array.isArray(enabled_mode_ids)
    ? enabled_mode_ids.map((v) => parseInt(v, 10)).filter((v) => !isNaN(v))
    : mode_id
    ? [parseInt(mode_id, 10)].filter((v) => !isNaN(v))
    : [];
  // Three explicit branches so the multi-valued path is unconditionally
  // scalar-suppressing (no fallback to a co-emitted mode_id from payload):
  //   1. length === 1 — emit scalar for legacy iPad / un-migrated Rails
  //      backward compat (the array and scalar agree, so no narrowing risk).
  //   2. length  >  1 — always omit the scalar; objectToFormData skips
  //      undefined keys, so Rails' reconcile_mode_state falls into its
  //      array-wins branch and derives mode_id from enabled_mode_ids.first
  //      without narrowing the persisted array. This MUST stay independent
  //      of `mode_id`'s presence in the input payload — a future form-state
  //      change that seeds mode_id alongside a multi-select would otherwise
  //      silently re-introduce the array-narrowing bug.
  //   3. length === 0 — no array at all; preserve any legacy payload's
  //      mode_id so an old caller still gets a coherent wire.
  const legacyModeId =
    enabledModeIds.length === 1
      ? enabledModeIds[0]
      : enabledModeIds.length > 1
      ? undefined
      : mode_id
      ? parseInt(mode_id, 10) || undefined
      : undefined;

  const incomingCustomization = channel_image_customization || {};
  // Strip the legacy boolean keys from the incoming customization snapshot
  // before re-deriving them — important because the form holds both the
  // array and the booleans concurrently, and we don't want a stale boolean
  // from `...incomingCustomization` to leak through when we're in multi-
  // style mode and intentionally omitting them.
  const {
    is_black_and_white_mode_enabled: _is_bw_omit,
    is_glitch_mode_enabled: _is_glitch_omit,
    ...customizationWithoutLegacyBooleans
  } = incomingCustomization;
  const enabledImageStyles = synthesizeEnabledImageStyles(incomingCustomization);
  const isMultiStyle = enabledImageStyles.length > 1;
  const firstStyle = enabledImageStyles[0];
  // Same single-valued vs. multi-valued split: only emit legacy booleans
  // when the user is in single-style mode. Multi-style sends just the
  // array; Rails' reconcile_image_style_state derives the booleans from
  // enabled_image_styles.first in its array-wins branch. Keeping the
  // booleans on a multi-style submission would flip
  // legacy_booleans_changed? on (e.g.) ["bw", "standard"] and silently
  // narrow the array to ["bw"].
  const channelImageCustomizationParams = {
    ...customizationWithoutLegacyBooleans,
    enabled_image_styles: enabledImageStyles,
    ...(isMultiStyle
      ? {}
      : {
          is_black_and_white_mode_enabled: firstStyle === "bw",
          is_glitch_mode_enabled: firstStyle === "glitch",
        }),
  };

  return {
    enabledModeIds,
    legacyModeId,
    channelImageCustomizationParams,
  };
};

export const getChannelDetail = (token, id) =>
  authedRequest(`v2/channels/${id}`, token).then((r) => {
    const enabled_mode_ids = synthesizeEnabledModeIds(r);
    const customization = r.channel_image_customization || null;
    const enabled_image_styles = synthesizeEnabledImageStyles(customization);
    return {
      ...r,
      big_logo_image_url:
        r.big_logo_image_url && r.big_logo_image_url.endsWith("missing.png")
          ? null
          : r.big_logo_image_url,
      channel_theme_instance: r.channel_theme_instance ? r.channel_theme_instance : {
        ...r.channel_theme_instance,
        with_photo_filter: false,
      },
      overlay_image:
        r.overlay_image_url && ['missing.png','overlay_image.png'].some(item => r.overlay_image_url.endsWith(item))
          ? null
          : r.overlay_image_url,
      mode_id: r.mode ? r.mode.id : null,
      enabled_mode_ids,
      channel_image_customization: customization
        ? { ...customization, enabled_image_styles }
        : { enabled_image_styles, is_black_and_white_mode_enabled: false, is_glitch_mode_enabled: false },
    };
  });

// DELETE /api/v2/channels/:id → ChannelsController#destroy (Pundit destroy?:
// owner OR super_admin). Irreversibly cascades the channel's gifs/images/faces/
// shares/leads/downloads. The route is PLURAL `channels` to match the rails v2
// namespace (config/routes.rb: `delete '/channels/:id'`); the prior singular
// `channel/:id` path had no matching route.
export const deleteChannel = (token, { channelId }) => {
  return (
    authedRequest(`v2/channels/${channelId}`, token, { method: 'DELETE' }).then(r => r));
}

// PATCH /api/v2/channels/:id with { channel: { archived: true } } →
// ChannelsController#update (Pundit update?: owner OR super_admin OR
// channel_admin). Archiving is a reversible flag flip, not a destructive op, so
// it rides the standard channel-update endpoint rather than a dedicated route.
export const archiveChannel = (token, { channelId }) => {
  return (
    authedRequest(`v2/channels/${channelId}`, token, {
      method: 'PATCH',
      body: JSON.stringify({ channel: { archived: true } }),
    }).then(r => r));
}

export const postChannel =
  (token, payload) => {
    // Multi-mode / multi-style: derive the legacy fields from the new arrays
    // so the create path matches the update path (patchChannel). Without this
    // a user picking multiple modes / styles in the New Channel form would
    // silently fall back to single-select on first save.
    const { enabledModeIds, legacyModeId, channelImageCustomizationParams } =
      buildModeAndStyleParams(payload);

    const params = objectToFormData({
      channel: {
        app_background_color: payload.background,
        ...payload,
        mode_id: legacyModeId,
        enabled_mode_ids: enabledModeIds,
        channel_theme_instance_attributes: {
          channel_theme_id: payload.channel_theme_id,
          first_line_text: payload.first_line_text,
          second_line_text: payload.second_line_text,
          first_line_font_shrink: payload.first_line_font_shrink,
          second_line_font_shrink: payload.second_line_font_shrink,
          with_photo_filter: payload.with_photo_filter,
          with_photo_filter_graphic: payload.with_photo_filter_graphic,
        },
        channel_image_customization: channelImageCustomizationParams,
        active_until: formatDateParam(payload.active_until),
        event_date: formatDateParam(payload.event_date),
      },
      client: {
        name: payload.client_name
      },
      time_zone: payload.time_zone,
      time_zone_offset: payload.timezone && (-1 * utcOffsetMinutesFor(payload.time_zone, new Date())),
      hashtag: payload.hashtag,
      company_promo: payload.company_promo,
      hashtag_enabled: payload.hashtag_enabled,
      company_promo_enabled: payload.company_promo_enabled,
    });

    return (
      authedFormRequest('v2/channels', token, {
        method: 'POST',
        body: params
      }).then(r => {
        return normalize(r, channel).entities}))
  }

export const formPatchChannel =
  (token, id, {
    client_name,
    channel_theme_id,
    first_line_text,
    second_line_text,
    with_photo_filter,
    with_photo_filter_graphic,
    ...params
  }) =>
    authedFormRequest(`v2/channels/${id}`, token, {
      method: 'PATCH',
      body: setFormData('channel', params)
    }).then(r => normalize(r, channel).entities);

export const patchChannel =
  (token, id, {
    client_name,
    channel_theme_id,
    channel_theme_instance,
    first_line_text,
    second_line_text,
    first_line_font_shrink,
    second_line_font_shrink,
    app_background_color,
    with_photo_filter,
    with_photo_filter_graphic,
    deleted_photo_filter_ids,
    ...rest
  }
) => {
  // Multi-mode / multi-style: shared derivation with postChannel (see
  // buildModeAndStyleParams). Keeps old iPads and un-migrated Rails
  // environments working by emitting both the new arrays and the legacy
  // single-value fields.
  const { enabledModeIds, legacyModeId, channelImageCustomizationParams } =
    buildModeAndStyleParams(rest);

  const params = objectToFormData({
    channel: {
      app_background_color,
      ...rest,
      mode_id: legacyModeId,
      enabled_mode_ids: enabledModeIds,
      channel_theme_instance_attributes: {
        id: channel_theme_instance.id,
        channel_theme_id,
        first_line_text,
        second_line_text,
        first_line_font_shrink,
        second_line_font_shrink,
        with_photo_filter,
        with_photo_filter_graphic,
      },
      channel_image_customization: channelImageCustomizationParams,
      active_until: formatDateParam(rest.active_until),
      event_date: formatDateParam(rest.event_date),
    },
    client: {
      name: client_name,
    },
  });
  // Explicit photo-filter deletion. Sent on EVERY update, unconditionally.
  //
  // The key's presence is the whole signal: it tells Rails this client states
  // its deletions outright, so a filter merely missing from `photo_filters` is
  // retained rather than soft-deleted. Absent, Rails falls back to inferring
  // deletion from absence — which cannot tell a deliberate removal apart from a
  // dropped reference, a partial save, or a stale payload, and once silently
  // unbranded a channel mid-event.
  //
  // The leading blank entry is load-bearing, not decoration: a genuinely empty
  // array does not survive form encoding (`{ids => []}` encodes to "" and
  // decodes back to `{}`), so the most common save — the user deleted nothing —
  // would otherwise arrive with the key absent and fall straight back to
  // inference. Rails strips the blanks. This is emitted here rather than left
  // to objectToFormData for exactly that reason.
  params.append("channel[deleted_photo_filter_ids][]", "");
  (Array.isArray(deleted_photo_filter_ids) ? deleted_photo_filter_ids : [])
    .filter((id) => id !== null && id !== undefined && String(id) !== "")
    .forEach((id) =>
      params.append("channel[deleted_photo_filter_ids][]", String(id)),
    );
  if (rest.big_logo_image_url === null) {
    params.append("channel[big_logo_image_url]", null);
  }
  if (rest.overlay_image === null) {
    params.append("channel[overlay_image]", null);
  }
  if (rest.gallery_banner === null) {
    params.append("channel[gallery_banner]", null);
  }
  if (rest.photo_filters === null) {
    // Send explicit clear signal alongside "null" so the backend can
    // distinguish intentional removal from an ambiguous empty state.
    // Deploy order: land the Rails clear_photo_filters guard first,
    // then this frontend change. The bare "null" is kept for backward
    // compatibility with pre-deploy backend sessions.
    params.append("channel[photo_filters]", null);
    params.append("channel[clear_photo_filters]", true);
  }
  return authedFormRequest(`v2/channels/${id}`, token, {
    method: "PATCH",
    body: params,
  }).then((r) => normalize(r, channel).entities);
};
